import React, { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { useAuth, useUser, useClerk } from '@clerk/react';
import { api } from '../api/client';
import type { Comment } from '../api/types';
import { useIsAdmin } from '../hooks/useIsAdmin';

interface CommentsProps {
  postSlug: string;
}

const Comments: React.FC<CommentsProps> = ({ postSlug }) => {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { openSignIn } = useClerk();
  const { isAdmin } = useIsAdmin();

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = useCallback(() => {
    api.comments
      .list(postSlug)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [postSlug]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken({ template: 'baldydev' });
      if (!token) throw new Error('Not authenticated');
      const comment = await api.comments.create(postSlug, text, token);
      setComments((prev) => [...prev, comment]);
      setDraft('');
    } catch (err: any) {
      setError(err.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      const token = await getToken({ template: 'baldydev' });
      if (!token) return;
      await api.comments.delete(postSlug, commentId, token);
      setComments((prev) => prev.filter((c) => c.commentId !== commentId));
    } catch (err) {
      console.error('Failed to delete comment', err);
    }
  };

  return (
    <section className="comments-section">
      <h2 className="comments-heading">Comments</h2>

      {loading ? (
        <p className="comments-loading">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="comments-empty">No comments yet — be the first!</p>
      ) : (
        <div className="comments-list">
          {comments.map((c) => (
            <div key={c.commentId} className="comment">
              <div className="comment-meta">
                <span className="comment-author">{c.userName || 'Anonymous'}</span>
                <span className="comment-date">
                  {format(new Date(c.createdAt), 'MMM d, yyyy')}
                </span>
                {(isAdmin || c.userId === user?.id) && (
                  <button
                    className="comment-delete"
                    onClick={() => handleDelete(c.commentId)}
                  >
                    Delete
                  </button>
                )}
              </div>
              <p className="comment-content">{c.content}</p>
            </div>
          ))}
        </div>
      )}

      <div className="comment-form-wrapper">
        {isSignedIn ? (
          <form className="comment-form" onSubmit={handleSubmit}>
            <textarea
              className="comment-textarea"
              placeholder="Write a comment…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              disabled={submitting}
            />
            {error && <p className="comment-error">{error}</p>}
            <button
              className="comment-submit"
              type="submit"
              disabled={submitting || !draft.trim()}
            >
              {submitting ? 'Posting…' : 'Post comment'}
            </button>
          </form>
        ) : (
          <div className="comment-sign-in-prompt">
            <button className="sign-in-button" onClick={() => openSignIn()}>
              Sign in to comment
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Comments;
